import type { SettlementFilters } from "~/lib/queries";

/**
 * Every filter on the settlements list, as URL search params.
 *
 * Same contract as the other lists: the URL is the only place these live, so a
 * filtered view is a link someone can paste into a finance thread.
 *
 * No status default, unlike webhook events and callbacks. Those screens open on
 * their failure state because that is why anyone visits them. A settlement has
 * no equivalent — nobody opens this screen to find broken settlements, they open
 * it to find a particular batch — and `settlements.status` is a free-form
 * varchar whose real values are unknown from here, so a default would be a
 * guess that silently hides rows.
 */
export interface SettlementSearch {
  psp_settlement_id?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
  limit?: number;
  offset?: number;
}

const FILTER_KEYS = [
  "psp_settlement_id",
  "status",
  "start_date",
  "end_date",
] as const satisfies readonly (keyof SettlementSearch)[];

export function validateSettlementSearch(raw: Record<string, unknown>): SettlementSearch {
  return {
    psp_settlement_id: str(raw.psp_settlement_id),
    status: str(raw.status),
    start_date: str(raw.start_date),
    end_date: str(raw.end_date),
    limit: num(raw.limit),
    offset: num(raw.offset),
  };
}

export function hasActiveFilters(search: SettlementSearch): boolean {
  return FILTER_KEYS.some((key) => search[key] !== undefined);
}

export function toFilters(search: SettlementSearch): SettlementFilters {
  return {
    psp_settlement_id: search.psp_settlement_id,
    status: search.status,
    start_date: search.start_date,
    end_date: search.end_date,
    limit: search.limit ?? 25,
    offset: search.offset ?? 0,
  };
}

function str(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function num(value: unknown): number | undefined {
  const parsed = typeof value === "number" ? value : Number(str(value));
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : undefined;
}
